import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { aiChatAPI, projectAPI, clientGroupAPI, dashboardCardAPI } from '../lib/api';
import { sortClientsAlpha } from '../lib/sortClients';
import { toast } from 'sonner';
import { StellarSelect } from '../components/StellarFormControls';
import {
  Sparkles, Plus, Send, Trash2, MoreHorizontal, Search, Loader2,
  FolderKanban, Building2, ChevronLeft, Edit3, Check, X, Pin, FileText,
  Zap, Eye, Code, Brain, MessageSquare as MsgIcon, Filter,
  Paperclip, Image as ImageIcon, Video, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  if (isToday) return time;
  const M = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  return `${M[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}, ${time}`;
}

const QUICK_ACTIONS = [
  { label: 'Plan my day', q: 'Plan my day based on current tasks and priorities. Be specific and actionable.', desc: 'Get a structured daily plan' },
  { label: 'Break down a task', q: 'Help me break down a complex task into manageable steps with time estimates.', desc: 'Split big tasks into steps' },
  { label: 'Suggest priorities', q: 'Look at my current tasks and suggest the best priority ordering. Explain your reasoning.', desc: 'Smart task ordering' },
  { label: 'Summarize status', q: 'Summarize the current status across all my projects and clients. Highlight what needs attention.', desc: 'Quick project overview' },
  { label: 'Draft a client email', q: 'Help me draft a professional client status update email with key milestones and next steps.', desc: 'Professional email writing' },
  { label: 'Weekly review prep', q: 'Help me prepare a weekly team review summary with wins, blockers, and focus areas for next week.', desc: 'Team standup prep' },
];

export default function AIChatPage() {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [models, setModels] = useState([]);
  const [model, setModel] = useState('openai/gpt-4o-mini');
  const [showNewSession, setShowNewSession] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newProjectId, setNewProjectId] = useState('');
  const [newClientId, setNewClientId] = useState('');
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [sessionSearch, setSessionSearch] = useState('');
  const [showMobile, setShowMobile] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showModelBrowser, setShowModelBrowser] = useState(false);
  const [modelFilter, setModelFilter] = useState('');
  const [modelProviderFilter, setModelProviderFilter] = useState('all');
  const [modelCapFilter, setModelCapFilter] = useState('all');
  const [showFreeOnly, setShowFreeOnly] = useState(false);
  const [modelHub, setModelHub] = useState(true); // Part 6: provider-tile hub is the default
  const [allModels, setAllModels] = useState([]);
  const [chatMode, setChatMode] = useState('text'); // text, image, video
  const [imageLoading, setImageLoading] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [attachedPreview, setAttachedPreview] = useState('');
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const wsId = currentWorkspace?.id;

  // Fetch sessions
  const fetchSessions = useCallback(async () => {
    if (!wsId) return;
    try {
      const res = await aiChatAPI.sessions(wsId);
      setSessions(res.data || []);
    } catch {}
    setLoading(false);
  }, [wsId]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  // Fetch ALL models + preferences
  useEffect(() => {
    aiChatAPI.models().then(res => {
      const all = res.data || [];
      setAllModels(all);
      // Set curated popular models for the quick selector
      const popular = all.filter(m =>
        ['openai/gpt-4o-mini', 'openai/gpt-4o', 'anthropic/claude-3.5-sonnet', 'anthropic/claude-3-haiku', 'google/gemini-2.0-flash-001', 'meta-llama/llama-3.1-70b-instruct', 'mistralai/mistral-large'].some(p => m.id === p)
      );
      setModels(popular.length > 0 ? popular : all.filter(m => !m.is_free).slice(0, 12));
    }).catch(() => {});
    aiChatAPI.preferences().then(res => { if (res.data?.default_model) setModel(res.data.default_model); }).catch(() => {});
  }, []);

  // Fetch projects and clients for context linking
  useEffect(() => {
    if (!wsId) return;
    projectAPI.list(wsId).then(res => setProjects(res.data || [])).catch(() => {});
    clientGroupAPI.list(wsId).then(res => setClients(sortClientsAlpha(res.data || []))).catch(() => {});
  }, [wsId]);

  // Load session messages
  useEffect(() => {
    if (!activeSession || !wsId) { setMessages([]); return; }
    aiChatAPI.getSession(wsId, activeSession.id).then(res => {
      setMessages(res.data?.messages || []);
    }).catch(() => {});
  }, [activeSession?.id, wsId]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    const msg = (text || msgText).trim();
    if (!msg || !activeSession || !wsId) return;
    setMsgText('');
    setSending(true);
    // Optimistic update
    const tempUser = { role: 'user', text: msg, model, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, tempUser]);
    try {
      const res = await aiChatAPI.sendMessage(wsId, activeSession.id, {
        message: msg, model, workspace_id: wsId, session_id: activeSession.id
      });
      // Replace optimistic with real + add AI response
      setMessages(prev => {
        const filtered = prev.filter(m => m !== tempUser);
        return [...filtered, res.data.user_msg, res.data.ai_msg];
      });
      // Update session title in list if it changed
      fetchSessions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI failed');
      setMessages(prev => prev.filter(m => m !== tempUser));
    }
    setSending(false);
  };

  const createSession = async () => {
    if (!wsId) return;
    try {
      // D78 — Frictionless start: no title/project/client required. AI
      // auto-titles from the first message (see server-side at
      // ai_session_message). Linking + sharing are surfaced inside the
      // chat as optional buttons.
      const res = await aiChatAPI.createSession(wsId, {
        workspace_id: wsId,
        title: 'New Chat',
        project_id: '',
        client_group_id: '',
      });
      setShowNewSession(false);
      setNewTitle('');
      setNewProjectId('');
      setNewClientId('');
      await fetchSessions();
      setActiveSession(res.data);
      setShowMobile(true);
    } catch { toast.error('Failed to create'); }
  };

  const deleteSession = async (sid) => {
    try {
      await aiChatAPI.deleteSession(wsId, sid);
      if (activeSession?.id === sid) { setActiveSession(null); setMessages([]); }
      fetchSessions();
    } catch {}
  };

  const renameSession = async () => {
    if (!editTitle.trim() || !activeSession) return;
    try {
      await aiChatAPI.updateSession(wsId, activeSession.id, { title: editTitle.trim() });
      setActiveSession(prev => ({ ...prev, title: editTitle.trim() }));
      fetchSessions();
    } catch {}
    setEditingTitle(false);
  };

  const pinToDashboard = async (msgText) => {
    if (!wsId) return;
    try {
      await dashboardCardAPI.add(wsId, {
        card_type: 'ai_insight',
        title: `AI: ${activeSession?.title || 'Insight'}`,
        size: 'medium',
        position: 0,
        config: { text: msgText, session_id: activeSession?.id, pinned_at: new Date().toISOString() },
      });
      toast.success('Pinned to dashboard');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to pin'); }
  };

  const generateSmartSummary = async () => {
    if (!wsId || !activeSession) return;
    setSummaryLoading(true);
    try {
      const res = await aiChatAPI.smartSummary(wsId, activeSession.id, {
        message: 'Generate smart summary', model, workspace_id: wsId
      });
      setMessages(prev => [...prev, res.data.user_msg, res.data.ai_msg]);
      fetchSessions();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to generate summary'); }
    setSummaryLoading(false);
  };

  const hasContext = activeSession?.project_id || activeSession?.client_group_id;

  const generateImage = async (text) => {
    const prompt = (text || msgText).trim();
    if (!prompt || !activeSession || !wsId) return;
    setMsgText('');
    setImageLoading(true);
    const tempUser = { role: 'user', text: `[Image] ${prompt}`, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, tempUser]);
    try {
      const res = await aiChatAPI.generateImage({ prompt, provider: 'openai', model: 'gpt-image-1' });
      const aiMsg = { role: 'assistant', text: '', image_base64: res.data.image_base64, model: res.data.model, timestamp: new Date().toISOString() };
      setMessages(prev => [...prev.filter(m => m !== tempUser), { ...tempUser }, aiMsg]);
      // Save to session
      await aiChatAPI.sendMessage(wsId, activeSession.id, { message: `[Generated image: ${prompt}]`, model: 'gpt-image-1', workspace_id: wsId });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Image generation failed');
      setMessages(prev => prev.filter(m => m !== tempUser));
    }
    setImageLoading(false);
    setChatMode('text');
  };

  const generateVideoFn = async (text) => {
    const prompt = (text || msgText).trim();
    if (!prompt || !activeSession || !wsId) return;
    setMsgText('');
    setImageLoading(true);
    const tempUser = { role: 'user', text: `[Video] ${prompt}`, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, tempUser]);
    try {
      const res = await aiChatAPI.generateVideo({ prompt, model: 'sora-2', size: '1280x720', duration: 4 });
      const jobId = res.data.job_id;
      // Add a pending message
      const pendingMsg = { role: 'assistant', text: `Generating video... (est. 2-5 min)\nJob: ${jobId}`, video_job_id: jobId, video_status: 'processing', timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, pendingMsg]);
      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await aiChatAPI.videoStatus(jobId);
          if (statusRes.data.status === 'done') {
            clearInterval(pollInterval);
            setMessages(prev => prev.map(m => m.video_job_id === jobId ? { ...m, text: 'Video generated successfully!', video_status: 'done', video_download_url: statusRes.data.download_url } : m));
            toast.success('Video generated!');
            setImageLoading(false);
          } else if (statusRes.data.status === 'error') {
            clearInterval(pollInterval);
            setMessages(prev => prev.map(m => m.video_job_id === jobId ? { ...m, text: `Video failed: ${statusRes.data.error || 'Unknown error'}`, video_status: 'error' } : m));
            setImageLoading(false);
          }
        } catch { /* keep polling */ }
      }, 10000); // Poll every 10 seconds
      // Save to session
      await aiChatAPI.sendMessage(wsId, activeSession.id, { message: `[Video generation: ${prompt}]`, model: 'sora-2', workspace_id: wsId });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Video generation failed');
      setMessages(prev => prev.filter(m => m !== tempUser));
      setImageLoading(false);
    }
    setChatMode('text');
  };

  const handleFileAttach = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachedFile(file);
    // Preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setAttachedPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setAttachedPreview('');
    }
  };

  const sendWithAttachment = async () => {
    if (!activeSession || !wsId) return;
    const prompt = msgText.trim() || 'Enhance this image';
    if (!attachedFile) { sendMessage(); return; }

    setMsgText('');
    setImageLoading(true);
    const tempUser = { role: 'user', text: `[Attached: ${attachedFile.name}] ${prompt}`, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, tempUser]);

    try {
      // For images, send to image enhancement
      if (attachedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        const b64 = await new Promise((resolve) => {
          reader.onload = (ev) => resolve(ev.target.result.split(',')[1]);
          reader.readAsDataURL(attachedFile);
        });
        const res = await aiChatAPI.generateImage({
          prompt, provider: 'gemini', model: 'gemini-3.1-flash-image-preview',
          reference_image: b64,
        });
        const aiMsg = { role: 'assistant', text: res.data.text || '', image_base64: res.data.image_base64, model: res.data.model, timestamp: new Date().toISOString() };
        setMessages(prev => [...prev.filter(m => m !== tempUser), { ...tempUser }, aiMsg]);
      } else {
        // For documents, just mention the attachment in context
        await sendMessage(`[User attached file: ${attachedFile.name} (${attachedFile.type})] ${prompt}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to process attachment');
      setMessages(prev => prev.filter(m => m !== tempUser));
    }
    setAttachedFile(null);
    setAttachedPreview('');
    setImageLoading(false);
  };

  const filteredSessions = sessions.filter(s =>
    !sessionSearch || s.title?.toLowerCase().includes(sessionSearch.toLowerCase())
  );

  const initials = (user?.name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="flex h-full bg-background" data-testid="ai-chat-page">
      {/* Session List */}
      <div className={`w-[280px] border-r border-border/30 flex flex-col shrink-0 ${showMobile && activeSession ? 'hidden md:flex' : 'flex'} ${!activeSession ? 'flex-1 md:flex-none' : ''}`}>
        <div className="h-12 flex items-center justify-between px-4 border-b border-border/30 shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-gold" />
            <span className="text-[13px] font-semibold">Stellar's AI</span>
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={createSession} data-testid="new-ai-session-btn">
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        <div className="px-3 py-2">
          <div className="flex items-center gap-2 h-[32px] px-2.5 rounded-md border border-border/20 focus-within:border-gold/30 transition-colors">
            <Search className="h-3.5 w-3.5 text-muted-foreground/30 shrink-0" />
            <input
              value={sessionSearch}
              onChange={e => setSessionSearch(e.target.value)}
              placeholder="Search conversations..."
              className="bg-transparent border-none outline-none text-[12px] flex-1 placeholder:text-muted-foreground/25"
              data-testid="ai-session-search"
            />
          </div>
        </div>

        <ScrollArea className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/30" />
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="text-center py-12 px-4">
              <Sparkles className="h-8 w-8 text-muted-foreground/10 mx-auto mb-3" />
              <p className="text-[12px] text-muted-foreground/40">No conversations yet</p>
              <Button variant="outline" size="sm" className="mt-3 text-[11px] h-7 border-border/30" onClick={createSession}>
                Start a conversation
              </Button>
            </div>
          ) : (
            <div className="px-1.5 py-1">
              {filteredSessions.map(s => {
                const isActive = activeSession?.id === s.id;
                const lastMsg = s.messages?.[s.messages.length - 1];
                return (
                  <div key={s.id} className="group relative">
                    <button
                      onClick={() => { setActiveSession(s); setShowMobile(true); }}
                      className={`flex items-start gap-2.5 w-full px-2.5 py-2.5 rounded-lg text-left transition-colors ${
                        isActive ? 'bg-foreground/[0.05]' : 'hover:bg-foreground/[0.02]'
                      }`}
                      data-testid={`ai-session-${s.id}`}
                    >
                      <Sparkles className="h-3.5 w-3.5 text-gold/40 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] truncate font-medium">{s.title}</p>
                        {(s.project_name || s.client_name) && (
                          <div className="flex items-center gap-1 mt-0.5">
                            {s.project_name && <span className="text-[9px] text-gold/40 flex items-center gap-0.5"><FolderKanban className="h-2.5 w-2.5" />{s.project_name}</span>}
                            {s.client_name && <span className="text-[9px] text-muted-foreground/30 flex items-center gap-0.5"><Building2 className="h-2.5 w-2.5" />{s.client_name}</span>}
                          </div>
                        )}
                        <p className="text-[10px] text-muted-foreground/30 mt-0.5">{formatTime(s.updated_at)}</p>
                      </div>
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="absolute right-2 top-2 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-foreground/[0.05] transition-opacity">
                          <MoreHorizontal className="h-3 w-3 text-muted-foreground/40" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-36">
                        <DropdownMenuItem onClick={() => deleteSession(s.id)} className="text-destructive text-[12px]">
                          <Trash2 className="h-3 w-3 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Chat Area */}
      <div className={`flex-1 flex flex-col min-w-0 ${showMobile && activeSession ? 'flex' : 'hidden md:flex'}`}>
        {activeSession ? (
          <>
            {/* Header */}
            <div className="h-12 flex items-center justify-between px-4 border-b border-border/30 shrink-0">
              <div className="flex items-center gap-3">
                <button className="md:hidden p-1 rounded hover:bg-foreground/[0.05]" onClick={() => setShowMobile(false)}>
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <Sparkles className="h-4 w-4 text-gold/60" />
                {editingTitle ? (
                  <div className="flex items-center gap-1">
                    <input value={editTitle} onChange={e => setEditTitle(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') renameSession(); }} className="h-6 text-[13px] bg-transparent border border-border/30 rounded px-2 outline-none w-48" autoFocus />
                    <button onClick={renameSession} className="p-0.5"><Check className="h-3.5 w-3.5 text-gold" /></button>
                    <button onClick={() => setEditingTitle(false)} className="p-0.5"><X className="h-3.5 w-3.5 text-muted-foreground" /></button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13px] font-medium">{activeSession.title}</span>
                    <button onClick={() => { setEditTitle(activeSession.title); setEditingTitle(true); }} className="p-0.5 opacity-0 hover:opacity-100 group-hover:opacity-50">
                      <Edit3 className="h-3 w-3 text-muted-foreground/30" />
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {hasContext && (
                  <Button variant="outline" size="sm" className="h-6 text-[10px] border-border/30 gap-1" onClick={generateSmartSummary} disabled={summaryLoading || sending} data-testid="smart-summary-btn">
                    {summaryLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
                    Smart Summary
                  </Button>
                )}
                <button
                  onClick={() => setShowModelBrowser(true)}
                  className="h-7 text-[11px] bg-foreground/[0.03] border border-border/30 rounded-lg px-3 outline-none flex items-center gap-2 hover:border-gold/30 transition-colors max-w-[220px]"
                  data-testid="ai-model-selector"
                >
                  <span className="truncate font-medium">{(allModels.find(m => m.id === model)?.name || model).split('/').pop()}</span>
                  <Filter className="h-3 w-3 text-muted-foreground/40 shrink-0" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              {messages.length === 0 && !sending ? (
                <div className="max-w-2xl mx-auto py-10">
                  <div className="text-center mb-8">
                    <Sparkles className="h-12 w-12 text-gold/20 mx-auto mb-4" />
                    <p className="text-[17px] font-display font-semibold text-muted-foreground/60">What can I help you with?</p>
                    {activeSession.project_name && (
                      <p className="text-[13px] text-gold/50 mt-2 flex items-center justify-center gap-1.5"><FolderKanban className="h-4 w-4" /> Working on: {activeSession.project_name}</p>
                    )}
                    {activeSession.client_name && (
                      <p className="text-[13px] text-muted-foreground/40 mt-1 flex items-center justify-center gap-1.5"><Building2 className="h-4 w-4" /> Client: {activeSession.client_name}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {QUICK_ACTIONS.map(a => (
                      <button key={a.label} onClick={() => { setMsgText(a.q); sendMessage(a.q); }}
                        className="p-4 rounded-xl border border-border/30 text-left hover:bg-foreground/[0.02] hover:border-gold/20 transition-all group"
                        data-testid={`quick-action-${a.label.replace(/\s/g, '-').toLowerCase()}`}
                      >
                        <p className="text-[13px] font-medium group-hover:text-gold transition-colors">{a.label}</p>
                        <p className="text-[11px] text-muted-foreground/35 mt-1">{a.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4 max-w-3xl mx-auto">
                  {messages.map((msg, i) => (
                    <div key={i} className={`flex gap-3 group ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                      {msg.role === 'user' ? (
                        <Avatar className="h-7 w-7 shrink-0 mt-0.5">
                          <AvatarFallback className="text-[9px] bg-gold/10 text-gold">{initials}</AvatarFallback>
                        </Avatar>
                      ) : (
                        <div className="h-7 w-7 rounded-full bg-gold/10 flex items-center justify-center shrink-0 mt-0.5">
                          <Sparkles className="h-3.5 w-3.5 text-gold/60" />
                        </div>
                      )}
                      <div className={`max-w-[80%] ${msg.role === 'user' ? 'text-right' : ''}`}>
                        {msg.role !== 'user' && msg.image_base64 ? (
                          <div className="rounded-xl overflow-hidden border border-border/20">
                            <img src={`data:image/png;base64,${msg.image_base64}`} alt="Generated" className="max-w-full max-h-[400px] object-contain" />
                            <a href={`data:image/png;base64,${msg.image_base64}`} download="stellar_generated.png" className="block text-[10px] text-gold hover:underline p-2 text-center">Download Image</a>
                          </div>
                        ) : msg.video_job_id ? (
                          <div className="p-3 rounded-xl border border-border/20 bg-foreground/[0.02]">
                            <div className="flex items-center gap-2 mb-1">
                              <Video className="h-4 w-4 text-gold/60" />
                              <span className="text-[12px] font-medium">
                                {msg.video_status === 'processing' ? 'Generating video...' : msg.video_status === 'done' ? 'Video ready' : 'Generation failed'}
                              </span>
                            </div>
                            <p className="text-[11px] text-muted-foreground/50">{msg.text}</p>
                            {msg.video_status === 'processing' && <div className="flex items-center gap-2 mt-2"><Loader2 className="h-3.5 w-3.5 animate-spin text-gold/40" /><span className="text-[10px] text-muted-foreground/30">This may take 2-5 minutes</span></div>}
                            {msg.video_status === 'done' && msg.video_download_url && (
                              <a href={process.env.REACT_APP_BACKEND_URL + msg.video_download_url} download className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-lg bg-gold/10 text-gold text-[11px] font-medium hover:bg-gold/20 transition-colors" data-testid="video-download-link">
                                <Download className="h-3 w-3" /> Download Video
                              </a>
                            )}
                          </div>
                        ) : (
                          <div className={`inline-block px-4 py-3 rounded-2xl text-[14px] leading-relaxed whitespace-pre-wrap ${
                            msg.role === 'user'
                              ? 'bg-gold/12 rounded-tr-sm'
                              : 'bg-foreground/[0.04] rounded-tl-sm'
                          }`}>
                            {/* D79 Phase 2 — strip the <action> tag from the body and render
                                a confirm card below it. The model can emit at most one
                                action per turn; multiple are ignored except the first. */}
                            {(() => {
                              const m = (msg.text || '').match(/<action>\s*(\{[\s\S]*?\})\s*<\/action>/);
                              if (!m) return msg.text;
                              return (msg.text || '').replace(m[0], '').trim();
                            })()}
                          </div>
                        )}
                        {/* D79 Phase 2 — Confirm card. Renders when the assistant
                            response contains a <action>{...}</action> block. */}
                        {msg.role === 'assistant' && (() => {
                          const m = (msg.text || '').match(/<action>\s*(\{[\s\S]*?\})\s*<\/action>/);
                          if (!m) return null;
                          let parsed;
                          try { parsed = JSON.parse(m[1]); } catch { return null; }
                          if (!parsed?.action) return null;
                          if (msg._action_done) {
                            return (
                              <div className="mt-2 px-3 py-2 rounded-md bg-emerald-500/8 border border-emerald-500/30 text-[11px] text-emerald-300"
                                data-testid={`ai-action-done-${i}`}>
                                ✓ Action completed: {parsed.action}
                              </div>
                            );
                          }
                          return (
                            <div className="mt-2 px-3 py-2.5 rounded-md bg-gold/8 border border-gold/30 text-[11.5px]"
                              data-testid={`ai-action-card-${i}`}>
                              <div className="font-medium text-gold/90 mb-1">AI suggests: {parsed.action.replace(/_/g, ' ')}</div>
                              <pre className="text-[10px] bg-foreground/[0.03] rounded p-2 mb-2 overflow-x-auto">{JSON.stringify(parsed.args || {}, null, 2)}</pre>
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  className="h-6 text-[10.5px] bg-gold/15 text-gold hover:bg-gold/25 border-0"
                                  onClick={async () => {
                                    try {
                                      await aiChatAPI.executeAction(wsId, {
                                        action: parsed.action,
                                        args: parsed.args || {},
                                        session_id: activeSession?.id,
                                      });
                                      setMessages(prev => prev.map((x, idx) => idx === i ? { ...x, _action_done: true } : x));
                                      toast.success('Action completed');
                                    } catch (err) {
                                      toast.error(err?.response?.data?.detail || 'Action failed');
                                    }
                                  }}
                                  data-testid={`ai-action-confirm-${i}`}
                                >
                                  Confirm
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 text-[10.5px]"
                                  onClick={() => setMessages(prev => prev.map((x, idx) => idx === i ? { ...x, _action_done: true } : x))}
                                  data-testid={`ai-action-cancel-${i}`}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          );
                        })()}
                        <div className="flex items-center gap-2 mt-0.5 mx-1">
                          <span className="text-[9px] text-muted-foreground/20">{formatTime(msg.timestamp)}</span>
                          {msg.model && msg.role === 'assistant' && <span className="text-[8px] text-muted-foreground/15">{msg.model.split('/').pop()}</span>}
                          {msg.role === 'assistant' && (
                            <button
                              onClick={() => pinToDashboard(msg.text)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5 text-[9px] text-muted-foreground/30 hover:text-gold"
                              title="Pin to Dashboard"
                              data-testid={`pin-msg-${i}`}
                            >
                              <Pin className="h-2.5 w-2.5" /> Pin
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {sending && (
                    <div className="flex items-center gap-3">
                      <div className="h-7 w-7 rounded-full bg-gold/10 flex items-center justify-center shrink-0">
                        <Sparkles className="h-3.5 w-3.5 text-gold/60" />
                      </div>
                      <div className="flex items-center gap-2 text-[12px] text-muted-foreground/40 py-2">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking...
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Input */}
            <div className="px-4 py-3 border-t border-border/30 shrink-0">
              {/* D78 — Image/Video tabs hidden. Backend endpoints kept dormant per owner choice. */}
              {/* Attached file preview */}
              {attachedFile && (
                <div className="flex items-center gap-2 mb-2 max-w-3xl mx-auto px-2 py-1.5 rounded-lg bg-foreground/[0.03] border border-border/20">
                  {attachedPreview ? (
                    <img src={attachedPreview} alt="" className="h-10 w-10 rounded object-cover" />
                  ) : (
                    <FileText className="h-5 w-5 text-muted-foreground/40" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] truncate">{attachedFile.name}</p>
                    <p className="text-[9px] text-muted-foreground/30">{(attachedFile.size / 1024).toFixed(0)} KB</p>
                  </div>
                  <button onClick={() => { setAttachedFile(null); setAttachedPreview(''); }} className="p-1 rounded hover:bg-foreground/[0.05]">
                    <X className="h-3 w-3 text-muted-foreground/40" />
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2 max-w-3xl mx-auto">
                <input type="file" ref={fileInputRef} onChange={handleFileAttach} className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.psd,.ai,.cdr,.png,.jpg,.jpeg,.gif,.svg,.webp" />
                <button onClick={() => fileInputRef.current?.click()} className="h-9 w-9 flex items-center justify-center rounded-md border border-border/20 hover:border-gold/30 hover:bg-gold/5 transition-colors shrink-0" title="Attach file" data-testid="attach-file-btn">
                  <Paperclip className="h-4 w-4 text-muted-foreground/40" />
                </button>
                <Input
                  value={msgText}
                  onChange={e => setMsgText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); attachedFile ? sendWithAttachment() : sendMessage(); } }}
                  placeholder="Ask anything about your CRM — clients, tasks, EODs, reviews…"
                  className="flex-1 h-10 text-[14px] bg-foreground/[0.02] border-border/20 focus:border-gold/30 rounded-lg"
                  disabled={sending || imageLoading}
                  data-testid="ai-message-input"
                />
                <Button size="sm" className="h-9 w-9 p-0 bg-gold/10 hover:bg-gold/20 text-gold border-0" onClick={() => attachedFile ? sendWithAttachment() : sendMessage()} disabled={(!msgText.trim() && !attachedFile) || sending || imageLoading} data-testid="ai-send-btn">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-md">
              <Sparkles className="h-14 w-14 text-gold/15 mx-auto mb-5" />
              <p className="text-[18px] text-muted-foreground/50 font-display font-semibold">Hey there, how can I help?</p>
              <p className="text-[14px] text-muted-foreground/30 mt-2 leading-relaxed">I'm your AI sidekick. I can plan your day, brainstorm ideas, generate images, create videos, write emails, or just chat. Pick a conversation or start fresh.</p>
              <Button variant="outline" size="default" className="mt-5 text-[13px] border-border/30 h-10 px-5" onClick={createSession} data-testid="start-ai-chat-btn">
                <Plus className="h-4 w-4 mr-2" /> Start a conversation
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* New Session Dialog — Dark themed, bigger text */}
      <Dialog open={showNewSession} onOpenChange={setShowNewSession}>
        <DialogContent className="sm:max-w-[440px] bg-card border-border/40" data-testid="new-ai-session-dialog">
          <DialogHeader>
            <DialogTitle className="text-[17px] font-display">Start a new chat</DialogTitle>
            <DialogDescription className="text-[13px] text-muted-foreground/50">What are we working on today? Link a project or client for smarter responses.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-[12px] text-muted-foreground/60 font-medium mb-1.5 block">Give it a name (optional)</label>
              <Input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="e.g. Q1 campaign brainstorm..." className="h-10 text-[13px] bg-foreground/[0.03] border-border/30 focus:border-gold/40" data-testid="ai-session-title-input" />
            </div>
            <div>
              <label className="text-[12px] text-muted-foreground/60 font-medium mb-1.5 block">Link to Project</label>
              <StellarSelect
                value={newProjectId || '__none__'}
                onValueChange={(v) => setNewProjectId(v === '__none__' ? '' : v)}
                options={[{ value: '__none__', label: 'No project selected' }, ...projects.map(p => ({ value: p.id, label: p.name }))]}
                testId="ai-session-project"
                className="h-10 text-[13px]"
              />
            </div>
            <div>
              <label className="text-[12px] text-muted-foreground/60 font-medium mb-1.5 block">Link to Client</label>
              <StellarSelect
                value={newClientId || '__none__'}
                onValueChange={(v) => setNewClientId(v === '__none__' ? '' : v)}
                options={[{ value: '__none__', label: 'No client selected' }, ...clients.map(c => ({ value: c.id, label: c.name }))]}
                testId="ai-session-client"
                className="h-10 text-[13px]"
              />
            </div>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" size="sm" onClick={() => setShowNewSession(false)} className="text-[13px] h-9 border-border/30">Cancel</Button>
            <Button size="sm" onClick={createSession} className="text-[13px] h-9 bg-gold/10 hover:bg-gold/20 text-gold border-0" data-testid="create-ai-session-btn">
              Let's go
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Model Browser Dialog — Redesigned */}
      <Dialog
        open={showModelBrowser}
        onOpenChange={(o) => {
          setShowModelBrowser(o);
          if (o) {
            // Always land on the provider hub + clear stale filters
            setModelHub(true);
            setModelProviderFilter('all');
            setModelCapFilter('all');
            setShowFreeOnly(false);
            setModelFilter('');
          }
        }}
      >
        <DialogContent className="sm:max-w-[680px] h-[80vh] flex flex-col bg-card border-border/40 overflow-hidden" data-testid="model-browser-dialog">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-[16px] font-display">
              {modelHub ? 'Pick a provider' : 'AI Model Catalog'}
            </DialogTitle>
            <DialogDescription className="text-[12px] text-muted-foreground/60">
              {modelHub
                ? `5 curated providers · ${allModels.length} total models available`
                : `${allModels.length} models available via OpenRouter`}
            </DialogDescription>
          </DialogHeader>

          {modelHub ? (
            <ProviderHub
              allModels={allModels}
              onPickProvider={(key) => {
                setModelProviderFilter(key);
                setModelHub(false);
              }}
              onShowAll={() => {
                setModelProviderFilter('all');
                setModelHub(false);
              }}
              currentModelId={model}
            />
          ) : (
            <>
              <div className="space-y-2.5 shrink-0">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setModelHub(true)}
                    className="h-[36px] px-3 rounded-lg border border-border/30 text-[11px] text-muted-foreground hover:text-gold hover:border-gold/30 transition-colors flex items-center gap-1 shrink-0"
                    data-testid="model-hub-back"
                  >
                    <ChevronLeft className="h-3 w-3" /> Providers
                  </button>
                  <div className="flex-1 flex items-center gap-2 h-[36px] px-3 rounded-lg border border-border/30 bg-foreground/[0.02] focus-within:border-gold/40">
                    <Search className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                    <input value={modelFilter} onChange={e => setModelFilter(e.target.value)} placeholder="Search models..." className="bg-transparent border-none outline-none text-[13px] flex-1 placeholder:text-muted-foreground/35" data-testid="model-search" />
                    {modelFilter && <button onClick={() => setModelFilter('')}><X className="h-3 w-3 text-muted-foreground/40" /></button>}
                  </div>
                  <button onClick={() => setShowFreeOnly(!showFreeOnly)} className={`h-[36px] px-4 rounded-lg text-[11px] font-semibold border transition-colors shrink-0 ${showFreeOnly ? 'border-green-500/40 bg-green-500/15 text-green-400' : 'border-border/30 text-muted-foreground/50 hover:border-border/50'}`} data-testid="free-filter-btn">
                    {showFreeOnly ? 'FREE Only' : 'Show Free'}
                  </button>
                </div>
                <div className="overflow-x-auto pb-1.5">
                  <div className="flex gap-1.5 w-max">
                    <button onClick={() => setModelProviderFilter('all')} className={`px-2.5 py-1 rounded-md text-[10px] font-medium whitespace-nowrap transition-colors ${modelProviderFilter === 'all' ? 'bg-gold/15 text-gold' : 'bg-foreground/[0.04] text-muted-foreground/50 hover:text-foreground/70'}`}>All ({allModels.length})</button>
                    {[...new Set(allModels.map(m => m.provider))].sort((a, b) => allModels.filter(m => m.provider === b).length - allModels.filter(m => m.provider === a).length).map(p => (
                      <button key={p} onClick={() => setModelProviderFilter(p)} className={`px-2.5 py-1 rounded-md text-[10px] font-medium whitespace-nowrap transition-colors ${modelProviderFilter === p ? 'bg-gold/15 text-gold' : 'bg-foreground/[0.04] text-muted-foreground/50 hover:text-foreground/70'}`}>
                        {p} ({allModels.filter(m => m.provider === p).length})
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-1.5">
                  {[{v:'all',l:'All Types'},{v:'chat',l:'Chat',i:MsgIcon},{v:'coding',l:'Coding',i:Code},{v:'vision',l:'Vision',i:Eye},{v:'thinking',l:'Thinking',i:Brain}].map(c => (
                    <button key={c.v} onClick={() => setModelCapFilter(c.v)} className={`px-2.5 py-1 rounded-md text-[10px] font-medium flex items-center gap-1 transition-colors ${modelCapFilter === c.v ? 'bg-gold/15 text-gold' : 'bg-foreground/[0.04] text-muted-foreground/50 hover:text-foreground/70'}`}>
                      {c.i && <c.i className="h-3 w-3" />} {c.l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto mt-3 -mx-3 px-3" data-testid="model-list-scroll">
                <div className="space-y-1">
                  {(() => {
                    const filtered = allModels
                      .filter(m => !modelFilter || m.name?.toLowerCase().includes(modelFilter.toLowerCase()) || m.id.toLowerCase().includes(modelFilter.toLowerCase()))
                      .filter(m => modelProviderFilter === 'all' || m.provider === modelProviderFilter)
                      .filter(m => modelCapFilter === 'all' || m.capabilities?.includes(modelCapFilter))
                      .filter(m => !showFreeOnly || m.is_free);
                    return (
                      <>
                        <p className="text-[10px] text-muted-foreground/40 px-1 py-1 sticky top-0 bg-card z-10">{filtered.length} models found</p>
                        {filtered.map(m => {
                          const isSelected = model === m.id;
                          return (
                            <button key={m.id} onClick={() => { setModel(m.id); aiChatAPI.updatePreferences({ default_model: m.id }).catch(() => {}); setShowModelBrowser(false); }}
                              className={`flex items-center gap-3 w-full p-3 rounded-xl text-left transition-all ${isSelected ? 'bg-gold/8 border border-gold/25' : 'hover:bg-foreground/[0.03] border border-transparent'}`}
                              data-testid={`model-${m.id.replace(/\//g, '-')}`}>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-[13px] font-medium">{m.name}</span>
                                  {m.is_free && <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-green-500/15 text-green-400 font-semibold shrink-0">FREE</span>}
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-[10px] text-muted-foreground/50 font-medium">{m.provider}</span>
                                  {m.capabilities?.map(c => (
                                    <span key={c} className={`text-[9px] px-1.5 py-0.5 rounded-md font-medium ${c === 'vision' ? 'bg-purple-500/15 text-purple-300' : c === 'coding' ? 'bg-blue-500/15 text-blue-300' : c === 'thinking' ? 'bg-amber-500/15 text-amber-300' : 'bg-foreground/[0.06] text-muted-foreground/40'}`}>{c}</span>
                                  ))}
                                  {m.context_length > 0 && <span className="text-[9px] text-muted-foreground/30">{Math.round(m.context_length / 1000)}k ctx</span>}
                                </div>
                              </div>
                              {isSelected && <Check className="h-4 w-4 text-gold shrink-0" />}
                            </button>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Part 6 — 5-provider hub lands you on a clean grid of the big labs
// (OpenAI, Anthropic, Google, Meta, xAI) + a "Show all N models" card
// for power users. Clicking any tile drops you into the filtered list.
// ──────────────────────────────────────────────────────────────────────
function ProviderHub({ allModels, onPickProvider, onShowAll, currentModelId }) {
  const providers = [
    { key: 'openai',     label: 'OpenAI',    tagline: 'GPT-4o, o1, image & voice',        accent: 'from-emerald-500/20 to-emerald-500/5',  textAccent: 'text-emerald-300/80' },
    { key: 'anthropic',  label: 'Anthropic', tagline: 'Claude Sonnet, Opus, Haiku',         accent: 'from-orange-500/20 to-orange-500/5',    textAccent: 'text-orange-300/80' },
    { key: 'google',     label: 'Google',    tagline: 'Gemini Pro, Flash, 2.0 Experimental',accent: 'from-blue-500/20 to-blue-500/5',        textAccent: 'text-blue-300/80' },
    { key: 'meta-llama', label: 'Meta',      tagline: 'Llama 3.1, 3.3 — open-weight',       accent: 'from-indigo-500/20 to-indigo-500/5',    textAccent: 'text-indigo-300/80' },
    { key: 'x-ai',       label: 'xAI',       tagline: 'Grok models by xAI',                 accent: 'from-zinc-500/20 to-zinc-500/5',        textAccent: 'text-zinc-200/80' },
  ];
  const providerCount = (key) => allModels.filter(m => (m.provider || '').toLowerCase() === key).length;
  const currentProvider = (currentModelId || '').split('/')[0];
  return (
    <div className="flex-1 overflow-y-auto mt-2" data-testid="model-provider-hub">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {providers.map(p => {
          const count = providerCount(p.key);
          const isActive = currentProvider === p.key;
          return (
            <button
              key={p.key}
              onClick={() => onPickProvider(p.key)}
              disabled={count === 0}
              className={`relative text-left p-5 rounded-xl border transition-all overflow-hidden group ${count === 0 ? 'border-border/20 opacity-40 cursor-not-allowed' : 'border-border/30 hover:border-gold/40 hover:-translate-y-0.5'} ${isActive ? 'border-gold/50 ring-1 ring-gold/30' : ''}`}
              data-testid={`provider-tile-${p.key}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${p.accent} pointer-events-none opacity-70`} />
              <div className="relative">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className={`text-[16px] font-display font-bold ${p.textAccent}`}>{p.label}</p>
                    <p className="text-[11px] text-muted-foreground/60 mt-0.5">{p.tagline}</p>
                  </div>
                  {isActive && <Check className="h-4 w-4 text-gold shrink-0" />}
                </div>
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/20">
                  <span className="text-[10px] text-muted-foreground/40 uppercase tracking-wider">
                    {count === 0 ? 'Unavailable' : `${count} model${count === 1 ? '' : 's'}`}
                  </span>
                  <span className="text-[10px] text-gold/70 group-hover:text-gold transition-colors">
                    Browse →
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <button
        onClick={onShowAll}
        className="mt-3 w-full p-4 rounded-xl border border-dashed border-border/30 text-center hover:border-gold/30 hover:bg-foreground/[0.02] transition-colors"
        data-testid="provider-show-all"
      >
        <p className="text-[12px] text-muted-foreground/80">
          Show all <span className="font-bold text-gold">{allModels.length}</span> models across all providers →
        </p>
        <p className="text-[10px] text-muted-foreground/40 mt-0.5">Power-user view: full list with search + capability filters</p>
      </button>
    </div>
  );
}
